import React, { useRef, useState, useEffect } from "react";
import { toast } from "@/state/hooks";
import { Paperclip, Smile, Mic, Square, Send, X } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { messageSchema, Message } from "@/data/schemas";
import { generateUUID as uuidv4, attachments, api, MessageType } from "@/core_bridge/module_api";
import GiphyPopup from "@/ui/components/GiphyPopup";
import { useStore } from "@/state/legacy_module_api";
import { WebAudioRecorder } from "@/utils/module_api";
import { appConfig } from "@/app/module_api";

interface ThreadMessagebarProps {
  replyToMessage?: Message | null;
  onClearReply?: () => void;
}

const stableObjectUrl = (uploadUrl: string): string => {
  try {
    const url = new URL(uploadUrl);
    url.search = "";
    return url.toString();
  } catch {
    return uploadUrl.split("?")[0] || uploadUrl;
  }
};

const ThreadMessagebar: React.FC<ThreadMessagebarProps> = ({
  replyToMessage,
  onClearReply,
}) => {
  const [messageText, setMessageText] = useState("");
  const [giphyPopupOpened, setGiphyPopupOpened] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const contactsById = useStore((s: any) => s.contactsById || {});
  const hiloEnv = useStore((s: any) => s.environment);
  const selection = useStore((s: any) => s.selection || {});
  const senderId = selection.senderId;
  const receiverId = selection.receiverId;
  const groupId = selection.groupId;
  const disableInput = selection.disableInput;
  const replySenderName = replyToMessage
    ? replyToMessage.senderId === senderId
      ? "Me"
      : contactsById[replyToMessage.senderId || ""]?.username ||
        replyToMessage.senderId ||
        "Unknown User"
    : "";
  const messageEnvironment =
    hiloEnv === "hi" || hiloEnv === "lo" ? hiloEnv : undefined;

  const handleMessageSend = () => {
    try {
      if (!messageText.trim()) return;

      const message = messageSchema.parse({
        id: uuidv4(),
        appId: appConfig.appId,
        type: MessageType.MESSAGE,
        status: "SENT",
        content: messageText,
        contentType: "TEXT",
        senderId,
        receiverId: receiverId || undefined,
        groupId: senderId && receiverId ? undefined : groupId || undefined,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        environment: messageEnvironment,
        replyToMessageId: replyToMessage?.id,
        replyToContent: replyToMessage?.content,
        replyToSenderId: replyToMessage?.senderId,
      });

      // Use threadService directly
      import("@/services/module_api")
        .then(({ threadService }) => threadService.sendMessage(message))
        .then(() => {
          setMessageText("");
          if (onClearReply) {
            onClearReply();
          }
        })
        .catch((err) => {
          console.error("Failed to send message", err);
          toast.error("Failed to send message");
        });
    } catch (e) {
      toast.error("Failed to send message");
      console.error(e);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    e.target.value = "";

    await uploadAndSendAttachment(file);
  };

  const uploadAndSendAttachment = async (file: File | Blob, duration?: number) => {
    try {
      toast.loading("Uploading...", { id: "attachment-upload" });

      // Read file as bytes
      const arrayBuffer = await file.arrayBuffer();
      const plaintext = new Uint8Array(arrayBuffer);

      // Encrypt the file using switch-core
      const encrypted = attachments.encrypt(plaintext);
      const encryptedData = encrypted.data;
      const keyBase64 = encrypted.key_base64;

      // Get presigned URL for upload (construct filename with uuid.enc)
      const uploadId = uuidv4();
      const presignedResponse = await api.getPresignedUrl(`attachment-${uploadId}.enc`);
      const uploadUrl = presignedResponse.url;
      const objectUrl = stableObjectUrl(uploadUrl);

      // Upload encrypted data to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: encryptedData,
        headers: {
          "Content-Type": "application/octet-stream",
          "x-amz-acl": "public-read",
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      // Determine content type
      const contentType = file.type.startsWith("image/")
        ? "IMAGE"
        : file.type.startsWith("video/")
          ? "VIDEO"
          : file.type.startsWith("audio/")
            ? "AUDIO"
            : "FILE";

      // Build attachment object
      const attachmentId = uuidv4();
      const attachment = {
        id: attachmentId,
        mimeType: file.type,
        objectUrl,
        encryptedUrl: objectUrl,
        keyBase64,
        filename: (file as File).name || `recording-${Date.now()}.webm`,
        size: file.size,
        ...(duration && { duration }),
      };

      // Create and send the message
      const message = messageSchema.parse({
        id: uuidv4(),
        appId: appConfig.appId,
        type: MessageType.MESSAGE,
        status: "SENT",
        content: JSON.stringify(attachment), // Also store as content for CLI compatibility
        contentType,
        attachment, // Set the attachment object for UI rendering
        senderId,
        receiverId: receiverId || undefined,
        groupId: senderId && receiverId ? undefined : groupId || undefined,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        environment: messageEnvironment,
      });

      const { threadService } = await import("@/services/module_api");
      await threadService.sendMessage(message);

      toast.dismiss("attachment-upload");
      toast.success("File sent!");
    } catch (error) {
      console.error("Failed to upload attachment:", error);
      toast.dismiss("attachment-upload");
      toast.error("Failed to upload file");
    }
  };

  const handleWalkieTalkieClick = async () => {
    if (!isRecording) {
      try {
        await WebAudioRecorder.startRecording({ format: "aac" });
        setIsRecording(true);
        setRecordingDuration(0);

        recordingTimerRef.current = setInterval(() => {
          setRecordingDuration((prev) => prev + 1);
        }, 1000);

        toast("Recording...", {
          description: "Click again to stop",
          duration: Infinity,
          id: "recording-toast",
        });
      } catch (error) {
        console.error("Failed to start recording:", error);
        toast.error("Failed to start recording. Please allow microphone access.");
      }
    } else {
      try {
        const { file, duration } = await WebAudioRecorder.stopRecording();

        setIsRecording(false);

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        toast.dismiss("recording-toast");

        await uploadAndSendAttachment(file, Math.round(duration));
      } catch (error) {
        console.error("Failed to stop recording:", error);
        toast.error("Failed to send voice message");
      }
    }
  };

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      toast.dismiss("recording-toast");
    };
  }, []);

  return (
    <>
      <div data-name="message-bar" className="p-3 border-t bg-background">
        {replyToMessage && (
          <div data-name="reply-preview" className="mb-2 px-3 py-2 bg-muted rounded-lg border-l-4 border-primary flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-primary">
                Replying to {replySenderName}
              </div>
              <div className="text-sm text-muted-foreground truncate">
                {replyToMessage.content || "Media message"}
              </div>
            </div>
            <Button
              data-name="clear-reply-button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onClearReply}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        <div data-name="message-input-row" className="flex items-center gap-2">
          <input
            disabled={!!disableInput}
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileSelect}
          />

          <Button
            data-name="attach-button"
            variant="ghost"
            size="icon"
            disabled={!!disableInput}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="w-5 h-5" />
          </Button>

          <Button
            data-name="giphy-button"
            variant="ghost"
            size="icon"
            disabled={!!disableInput}
            onClick={() => setGiphyPopupOpened(true)}
          >
            <Smile className="w-5 h-5" />
          </Button>

          <Input
            data-name="message-input"
            disabled={!!disableInput}
            placeholder={
              disableInput ? disableInput.reason : "Type a message..."
            }
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            className="flex-1 min-w-0"
          />

          <Button
            data-name="voice-button"
            variant="ghost"
            size="icon"
            disabled={!!disableInput}
            onClick={handleWalkieTalkieClick}
            className={isRecording ? "text-destructive" : ""}
          >
            {isRecording ? (
              <Square className="w-5 h-5" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </Button>

          <Button
            data-name="send-button"
            variant="ghost"
            size="icon"
            disabled={!!disableInput}
            onClick={handleMessageSend}
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <GiphyPopup
        opened={giphyPopupOpened}
        onPopupClose={() => setGiphyPopupOpened(false)}
        onGifSelect={(src) => {
          const message = messageSchema.parse({
            id: uuidv4(),
            appId: appConfig.appId,
            type: MessageType.MESSAGE,
            status: "SENT",
            content: src,
            contentType: "GIPHY",
            senderId,
            receiverId: receiverId || undefined,
            groupId: senderId && receiverId ? undefined : groupId || undefined,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            environment: messageEnvironment,
          });

          import("@/services/module_api")
            .then(({ threadService }) => threadService.sendMessage(message))
            .then(() => toast.success("GIF sent!"))
            .catch((err) => {
              console.error("Failed to send GIF", err);
              toast.error("Failed to send GIF");
            });
        }}
        title="Send a GIF"
      />
    </>
  );
};

export default ThreadMessagebar;
